// Beveiliging rond de HTML5HistoryManager van het `navigation`-pakket (gebruikt
// door react-native-ridge-navigation op web).
//
// Probleem (Sentry WEBAPP-4/F/10, "The Url / is invalid"): de eerste
// history-entry van een paginabezoek is door de browser gemaakt en heeft geen
// `navigationLink` in history.state. Bij "/" (geen route) of een kapotte
// deep link navigeert ridge naar de standaard-URL (/app/home) met een
// pushState, waardoor die onbruikbare entry onder de app blijft liggen. Gaat
// de gebruiker terug, dan parseert de popstate-handler location ("/") en
// gooit een onafgevangen fout.
//
// Oplossing:
// 1. addHistory: zolang de huidige entry nog niet van de navigator is (geen
//    navigationLink), vervangen we hem in plaats van er een bovenop te zetten.
//    Zo verdwijnt "/" of een kapotte URL uit de history.
// 2. popstate: kan een entry toch niet geparsed worden, dan vallen we terug
//    op de standaard-URL in plaats van een fout te gooien.

export type NavigateHistory = (url?: string) => void;

export interface HistoryManagerInstance {
  disabled: boolean;
  getHref(url: string): string;
}

export interface HistoryManagerPrototype {
  init(this: HistoryManagerInstance, navigateHistory: NavigateHistory, rewriteUrl?: unknown): void;
  addHistory(this: HistoryManagerInstance, url: string, replace?: boolean, ...rest: unknown[]): void;
}

export interface HistoryLike {
  readonly state: unknown;
  replaceState(data: unknown, unused: string, url?: string | null): void;
}

export interface HistoryGuardOptions {
  prototype: HistoryManagerPrototype;
  // Alleen de hoofd-navigator (HTML5HistoryManager zelf) beheert de echte
  // URL; kopieën krijgen een HashHistoryManager en laten we met rust.
  isRootManager: (manager: HistoryManagerInstance) => boolean;
  getHistory: () => HistoryLike;
  fallbackUrl: string;
}

const GUARDED = "__dgvHistoryGuard";

export function hasNavigationLink(state: unknown): boolean {
  return (
    typeof state === "object" &&
    state !== null &&
    typeof (state as { navigationLink?: unknown }).navigationLink === "string" &&
    (state as { navigationLink: string }).navigationLink !== ""
  );
}

function stateWithLink(state: unknown, url: string) {
  const base = typeof state === "object" && state !== null ? state : {};
  return { ...base, navigationLink: url };
}

export function installHistoryGuard({ prototype, isRootManager, getHistory, fallbackUrl }: HistoryGuardOptions): boolean {
  const marked = prototype as HistoryManagerPrototype & { [GUARDED]?: boolean };
  if (marked[GUARDED]) {
    return false;
  }
  marked[GUARDED] = true;

  const originalAddHistory = prototype.addHistory;
  prototype.addHistory = function (url, replace, ...rest) {
    if (!this.disabled && isRootManager(this)) {
      const history = getHistory();
      if (!hasNavigationLink(history.state)) {
        history.replaceState(stateWithLink(history.state, url), "", this.getHref(url));
        return;
      }
    }
    return originalAddHistory.call(this, url, replace, ...rest);
  };

  const originalInit = prototype.init;
  prototype.init = function (navigateHistory, rewriteUrl) {
    const manager = this;
    const guarded: NavigateHistory = (url) => {
      try {
        navigateHistory(url);
      } catch (error) {
        if (!isRootManager(manager)) {
          throw error;
        }
        console.warn("[history-guard] onbekende URL in history, terug naar", fallbackUrl, error);
        const history = getHistory();
        history.replaceState(stateWithLink(history.state, fallbackUrl), "", manager.getHref(fallbackUrl));
        navigateHistory(fallbackUrl);
      }
    };
    return originalInit.call(this, guarded, rewriteUrl);
  };

  return true;
}
